import { Controller } from "@hotwired/stimulus";

export default class ConfirmationController extends Controller {
  static values = {
    url: String,
    turbo: Boolean,
    method: { type: String, default: "delete" },
    title: String,
    text: String,
    icon: String,
    confirmText: String,
    confirmColor: String,
    denyText: String,
    denyColor: String,
    cancelColor: String,
    redirectUrl: String,
    class: String,
    titleClass: String,
    manualRedirect: { type: Boolean, default: false },
    html: { type: Boolean, default: false }
  };

  connect() {
    this.element.addEventListener("click", this.handleClick);
  }

  disconnect() {
    this.element.removeEventListener("click", this.handleClick);
    this.clearTranslationMask?.();
  }

  handleClick = async (event) => {
    event.preventDefault();
    if (this.confirmationPending) return;
    this.confirmationPending = true;
    this.capturePostAnchor();
    try {
      await this.confirm(event);
    } finally {
      this.confirmationPending = false;
    }
  };

  capturePostAnchor() {
    this.scrollContainer = this.element.closest(".post-feed-scroll");
    if (!this.scrollContainer) return;

    const rootTop = this.scrollContainer.getBoundingClientRect().top;
    const clickedPost = this.element.closest(".feed-post-frame");
    const visiblePost = Array.from(
      this.scrollContainer.querySelectorAll(".feed-post-frame")
    ).find(post => post.getBoundingClientRect().bottom > rootTop);

    this.postAnchor = visiblePost === clickedPost
      ? clickedPost.nextElementSibling || clickedPost.previousElementSibling
      : visiblePost;
    this.postAnchorOffset = this.postAnchor?.getBoundingClientRect().top - rootTop;
  }

  restorePostAnchor() {
    if (!this.postAnchor?.isConnected || !this.scrollContainer?.isConnected) return;

    const rootTop = this.scrollContainer.getBoundingClientRect().top;
    const currentOffset = this.postAnchor.getBoundingClientRect().top - rootTop;
    this.scrollContainer.scrollTop += currentOffset - this.postAnchorOffset;
  }

  hideAllTooltip() {
    for (const tooltip of document.querySelectorAll(".tooltip")) {
      tooltip.remove();
    }
  }

  async confirm(event) {
    const form = event.target.closest("form");
    const mainUrl = this.urlValue || event.currentTarget.getAttribute("href");

    if (form && typeof $(form).valid === "function" && !$(form).valid()) return;

    const hasDenyButton = Boolean(this.denyTextValue);
    const texts = [
      this.titleValue || "Are you sure?",
      this.textValue || "You won't be able to revert this!",
      this.confirmTextValue || "Yes",
      this.denyTextValue || "Exit & Delete",
      "Cancel"
    ];
    const [title, text, confirmText, denyText, cancelText] = texts;
    if (!this.element.isConnected) return;

    return Swal.fire({
      customClass: {
        popup: `swal-popup align-items-center justify-content-center w-100 ${this.classValue || "alert-sm-bg"}`,
        title: `${this.titleClassValue || ""}`,
        htmlContainer: "text-black-paint text-white",
      },
      animation: false,
      heightAuto: false,
      title,
      text,
      icon: this.iconValue || "warning",
      showCancelButton: true,
      showDenyButton: hasDenyButton,
      allowOutsideClick: false,
      allowEscapeKey: false,
      confirmButtonColor: this.confirmColorValue || "#3085d6",
      denyButtonColor: this.denyColorValue || "#d33",
      cancelButtonColor: this.cancelColorValue || "#6c757d",
      confirmButtonText: confirmText,
      denyButtonText: denyText,
      cancelButtonText: cancelText,
      willOpen: (popup) => this.maskPendingTranslation(popup),
      didClose: () => this.clearTranslationMask?.(),
      didOpen: () => {
        this.hideAllTooltip();
      }
    }).then(result => {
      console.log(result);
      if (result.isConfirmed) {
        const dispatchEvent = event?.target?.dataset?.dispatchEvent;
        if (dispatchEvent) this.dispatchEvent(dispatchEvent);

        form ? form.requestSubmit() : this.sendRequest(mainUrl);
        return;
      }

      if (result.isDismissed) {
        setTimeout(() => {
          for (const element of document.querySelectorAll("[data-bs-original-title][aria-describedby]")) {
            const tooltip = bootstrap.Tooltip.getInstance(element);

            if (tooltip) {
              tooltip.hide();
            }
          }
        }, 300);
      }

      if (result.isDenied) {
        const dispatchEvent = event?.target?.dataset?.dispatchEvent;
        if (dispatchEvent) this.dispatchEvent(dispatchEvent);

        // Append parameter delete_chat=true to the target URL
        const deleteUrl = new URL(mainUrl, globalThis.location.origin);
        deleteUrl.searchParams.set("delete_chat", "true");

        this.sendRequest(deleteUrl.toString());
        return;
      }

      const disabledClass = this.element?.dataset?.disabledElement;
      if (!disabledClass) return;

      const disabledElement = this.element.closest("." + disabledClass);
      if (disabledElement) {
        disabledElement.style.cursor = "pointer";
        const disableController = this.application.getControllerForElementAndIdentifier(disabledElement, "disable-click");
        disableController?.enable();
      }
    });
  }

  maskPendingTranslation(popup) {
    this.clearTranslationMask?.();
    if ((localStorage.getItem("selected_lang") || "en") === "en") return;

    // Keep the Google widget's normal DOM translation enabled, and hide only
    // the initial paint until the translated message settles.
    const message = popup.querySelector(".swal2-html-container");
    if (!message) return;
    const originalText = message.textContent;
    popup.style.opacity = "0";
    let settledTimer;
    let fallbackTimer;
    const reveal = () => {
      observer.disconnect();
      globalThis.clearTimeout(settledTimer);
      globalThis.clearTimeout(fallbackTimer);
      popup.style.opacity = "";
      this.clearTranslationMask = null;
    };
    const observer = new MutationObserver(() => {
      globalThis.clearTimeout(settledTimer);
      if (message.textContent.trim() && message.textContent !== originalText) {
        settledTimer = globalThis.setTimeout(reveal, 250);
      }
    });
    observer.observe(popup, { childList: true, characterData: true, subtree: true });
    // Keep sign-out usable when the translation widget cannot load.
    fallbackTimer = globalThis.setTimeout(reveal, 12000);
    this.clearTranslationMask = reveal;
  }

  async sendRequest(url) {
    const turboRequest = await this.loadTurboRequest();
    await turboRequest(url, {
      method: this.methodValue.toUpperCase(),
      turboStream: this.turboValue,
      html: this.htmlValue,
      manualRedirect: this.manualRedirectValue,
      onSuccess: (result, response) => {
        if (this.manualRedirectValue && this.hasRedirectUrlValue) {
          globalThis.location.href = this.redirectUrlValue;
          return;
        }

        if (this.turboValue) {
          requestAnimationFrame(() => {
            this.restorePostAnchor();
            requestAnimationFrame(() => this.restorePostAnchor());
          });
        } else if (this.hasRedirectUrlValue) {
          globalThis.location.href = this.redirectUrlValue;
        }
      }
    });
  }

  dispatchEvent(name, detail = {}) {
    const event = new CustomEvent(name, { detail });
    document.dispatchEvent(event);
  }

  async loadTurboRequest() {
    const turboRequest = (await import("../helpers/api")).turboRequest;
    return turboRequest;
  }
}
